/**
 * Мост к Telegram Mini App · Ф2.2
 *
 * Здесь собрано ВСЁ общение с клиентом Telegram, чтобы остальной код не знал про SDK:
 *   • ready() + expand() сразу на старте (на ПК expand — тихий no-op, вреда нет);
 *   • высота: реальную даёт --tg-viewport-stable-height / viewportStableHeight,
 *     100vh внутри нативного BottomSheet врёт → кладём свою --app-h;
 *   • тема: светлую/тёмную решаем САМИ по яркости фона Telegram (флаг colorScheme
 *     считает SDK и на кастомной теме он переворачивается), пользовательская
 *     настройка light/dark всегда сильнее;
 *   • хаптик: поддержку проверяем в рантайме и ВСЕГДА дублируем визуально —
 *     на Desktop/Web вибрация это тихий no-op, isVersionAtLeast не спасает;
 *   • свои вертикальные скроллы → disableVerticalSwipes(), иначе жест закроет приложение.
 *
 * Факты и оговорки по клиентам — knowledge/mini-app-surfaces.md.
 */

export type ThemePref = "light" | "dark" | "auto";
export type Theme = "light" | "dark";

interface ThemeParams {
  bg_color?: string;
  secondary_bg_color?: string;
  [key: string]: string | undefined;
}

interface HapticApi {
  impactOccurred?: (style: string) => void;
  notificationOccurred?: (type: string) => void;
  selectionChanged?: () => void;
}

interface BackButtonApi {
  show?: () => void;
  hide?: () => void;
  onClick?: (cb: () => void) => void;
  offClick?: (cb: () => void) => void;
}

interface WebApp {
  initData?: string;
  version?: string;
  platform?: string;
  colorScheme?: string;
  themeParams?: ThemeParams;
  viewportHeight?: number;
  viewportStableHeight?: number;
  isExpanded?: boolean;
  HapticFeedback?: HapticApi;
  BackButton?: BackButtonApi;
  ready?: () => void;
  expand?: () => void;
  isVersionAtLeast?: (v: string) => boolean;
  onEvent?: (name: string, cb: (payload?: unknown) => void) => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  setBottomBarColor?: (color: string) => void;
  disableVerticalSwipes?: () => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: WebApp };
  }
}

const webApp: WebApp | undefined = window.Telegram?.WebApp;

/** Запущены ли мы внутри клиента Telegram (снаружи — режим разработки в браузере). */
export function insideTelegram(): boolean {
  return !!webApp?.initData;
}

export function platform(): string {
  return webApp?.platform || "unknown";
}

/**
 * Подпись для API. Внутри Telegram — настоящая initData.
 * Снаружи (браузер на ПК, отладка) — только в dev-сборке: строка из
 * `python api.py --dev-token` через ?dev_init=… или VITE_DEV_INIT_DATA.
 * В боевой сборке эта лазейка физически отсутствует.
 */
export function initData(): string {
  if (webApp?.initData) return webApp.initData;
  if (import.meta.env.DEV) {
    const fromUrl = new URLSearchParams(location.search).get("dev_init");
    if (fromUrl) return fromUrl;
    const fromEnv = import.meta.env.VITE_DEV_INIT_DATA as string | undefined;
    if (fromEnv) return fromEnv;
  }
  return "";
}

/* ── высота ────────────────────────────────────────────────────────────────── */

function stableHeight(): number {
  const css = getComputedStyle(document.documentElement)
    .getPropertyValue("--tg-viewport-stable-height").trim();
  const fromCss = css.endsWith("px") ? parseFloat(css) : NaN;
  if (Number.isFinite(fromCss) && fromCss > 200) return fromCss;
  if (webApp?.viewportStableHeight && webApp.viewportStableHeight > 200) return webApp.viewportStableHeight;
  return window.innerHeight;
}

function syncHeight(): void {
  document.documentElement.style.setProperty("--app-h", `${Math.round(stableHeight())}px`);
}

/* ── тема ──────────────────────────────────────────────────────────────────── */

/** Яркость по формуле SDK (HSP): темнее 120 — считаем фон тёмным. */
function isDarkHex(hex?: string): boolean | null {
  if (!hex) return null;
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const hsp = Math.sqrt(0.299 * r * r + 0.587 * g * g + 0.114 * b * b);
  return hsp < 120;
}

/** Настройка пользователя → фактическая тема. `auto` = как в Telegram, иначе как в системе. */
export function resolveTheme(pref: ThemePref): Theme {
  if (pref === "light" || pref === "dark") return pref;
  const byTelegram = isDarkHex(webApp?.themeParams?.bg_color);
  if (byTelegram !== null) return byTelegram ? "dark" : "light";
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** Поставить тему на документ и покрасить хром Telegram нашими же цветами. */
export function applyTheme(pref: ThemePref): Theme {
  const theme = resolveTheme(pref);
  document.documentElement.setAttribute("data-theme", theme);
  const stage = getComputedStyle(document.documentElement).getPropertyValue("--stage").trim();
  const nav = getComputedStyle(document.documentElement).getPropertyValue("--nav-bg").trim();
  try {
    webApp?.setBackgroundColor?.(stage);
    webApp?.setHeaderColor?.(stage);
    webApp?.setBottomBarColor?.(nav);
  } catch {
    /* старый клиент / метод не поддержан — вид от этого не ломается */
  }
  return theme;
}

/* ── отклик на нажатие ─────────────────────────────────────────────────────── */

/** Визуальный отклик — всегда; вибрация — если клиент реально умеет. */
export function tap(el?: Element | null, kind: "light" | "medium" | "success" = "light"): void {
  if (el) {
    el.classList.remove("tapped");
    void (el as HTMLElement).offsetWidth;   // перезапуск анимации
    el.classList.add("tapped");
    setTimeout(() => el.classList.remove("tapped"), 260);
  }
  try {
    const h = webApp?.HapticFeedback;
    if (!h) return;
    if (kind === "success") h.notificationOccurred?.("success");
    else h.impactOccurred?.(kind);
  } catch {
    /* на Desktop/Web хаптика нет — визуальный отклик уже случился */
  }
}

/* ── системная кнопка «назад» ──────────────────────────────────────────────── */

let backHandler: (() => void) | null = null;

export function setBackButton(visible: boolean, onClick?: () => void): void {
  const bb = webApp?.BackButton;
  if (!bb) return;
  try {
    if (backHandler) { bb.offClick?.(backHandler); backHandler = null; }
    if (visible && onClick) {
      backHandler = onClick;
      bb.onClick?.(backHandler);
      bb.show?.();
    } else {
      bb.hide?.();
    }
  } catch {
    /* нет кнопки — в приложении есть своя навигация */
  }
}

/* ── старт ─────────────────────────────────────────────────────────────────── */

/**
 * Поднять мост: ready → expand → высота → подписки.
 * @param onThemeChanged дёргается, когда пользователь сменил тему В Telegram
 *                       (нужно, только если у него выбрано «как в Telegram»).
 */
export function initTelegram(onThemeChanged?: () => void): void {
  try {
    webApp?.ready?.();
    webApp?.expand?.();
    if (webApp?.isVersionAtLeast?.("7.7")) webApp.disableVerticalSwipes?.();
  } catch {
    /* вне Telegram методов нет — работаем как обычная веб-страница */
  }

  syncHeight();
  // tdesktop: isStateStable всегда true; Web A присылает нестабильные фазы;
  // Web K событие вообще не реализует — поэтому ещё и window.resize.
  try {
    webApp?.onEvent?.("viewportChanged", (payload) => {
      const stable = (payload as { isStateStable?: boolean } | undefined)?.isStateStable;
      if (stable === false) return;
      syncHeight();
    });
    webApp?.onEvent?.("themeChanged", () => onThemeChanged?.());
    webApp?.onEvent?.("safeAreaChanged", syncHeight);
  } catch {
    /* без событий обойдёмся фолбэком ниже */
  }
  window.addEventListener("resize", syncHeight);
  window.addEventListener("orientationchange", syncHeight);
  window.matchMedia?.("(prefers-color-scheme: dark)")
    .addEventListener?.("change", () => onThemeChanged?.());
}
